import {
  IonContent,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonModal,
  IonItem,
  IonLabel,
  IonInput,
  IonButton,
  IonList,
  IonSearchbar,
  IonSpinner,
  IonText,
} from "@ionic/react";
import { addCircleOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { createClient } from "@/api/projects";
import { useCreateProjectMutation } from "@/hooks/useProjects";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { AppHeader } from "@/components/AppHeader";
import type { Client, Project } from "@/api/projects";
import type { Did, PaginatedResponse } from "@/types";

interface CreateProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (project: Project) => void;
}

export const CreateProjectModal = ({
  isOpen,
  onClose,
  onCreated,
}: CreateProjectModalProps) => {
  const [step, setStep] = useState<"client" | "newClient" | "details">(
    "client"
  );
  const [clientSearch, setClientSearch] = useState("");
  const [client, setClient] = useState<Client | null>(null);
  const [street, setStreet] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [city, setCity] = useState("");
  const [name, setName] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // New-client form state
  const [newClientName, setNewClientName] = useState("");
  const [newClientStreet, setNewClientStreet] = useState("");
  const [newClientZipcode, setNewClientZipcode] = useState("");
  const [newClientCity, setNewClientCity] = useState("");
  const [creatingClient, setCreatingClient] = useState(false);

  const queryClient = useQueryClient();
  const createProjectMutation = useCreateProjectMutation();

  // Reset state on every open
  useEffect(() => {
    if (isOpen) {
      setStep("client");
      setClientSearch("");
      setClient(null);
      setStreet("");
      setZipcode("");
      setCity("");
      setName("");
      setErrorMessage(null);
      setNewClientName("");
      setNewClientStreet("");
      setNewClientZipcode("");
      setNewClientCity("");
    }
  }, [isOpen]);

  // Server-side search met infinite scroll. Bij intypen reset de queryKey
  // (nieuwe debouncedSearch) → nieuwe pagina-1 fetch. React Query cached
  // 30s zodat heen-en-weer typen niet stoort.
  const debouncedSearch = useDebouncedValue(clientSearch, 250);

  const {
    data: clientsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: clientsLoading,
  } = useInfiniteQuery({
    queryKey: ["clients", "modal", debouncedSearch],
    queryFn: async ({ pageParam = 1 }) => {
      const { data } = await apiClient.get<PaginatedResponse<Client>>(
        "/clients",
        {
          params: {
            page: pageParam,
            search: debouncedSearch || undefined,
            per_page: 25,
          },
        }
      );
      return data;
    },
    getNextPageParam: (last) =>
      last.current_page < last.last_page ? last.current_page + 1 : undefined,
    initialPageParam: 1,
    enabled: isOpen,
    staleTime: 30_000,
  });

  const allClients = useMemo<Client[]>(
    () => clientsData?.pages.flatMap((page) => page.data) ?? [],
    [clientsData]
  );

  const pickClient = (c: Client) => {
    setClient(c);
    setStreet(c.address_json?.street ?? "");
    setZipcode(c.address_json?.zipcode ?? "");
    setCity(c.address_json?.city ?? "");
    setStep("details");
  };

  const handleCreateClient = async () => {
    if (!newClientName.trim()) {
      setErrorMessage("Vul een naam in voor de klant.");
      return;
    }

    setCreatingClient(true);
    setErrorMessage(null);

    try {
      const created = await createClient({
        name: newClientName.trim(),
        address_json: {
          street: newClientStreet.trim() || undefined,
          zipcode: newClientZipcode.trim() || undefined,
          city: newClientCity.trim() || undefined,
          country: "NL",
        },
      });
      // Invalidate so the list reloads next time
      queryClient.invalidateQueries({ queryKey: ["clients", "all"] });
      pickClient(created);
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Klant kon niet worden aangemaakt.";
      setErrorMessage(message);
    } finally {
      setCreatingClient(false);
    }
  };

  const handleSubmit = async () => {
    setErrorMessage(null);

    if (!client) {
      setErrorMessage("Kies eerst een klant.");
      return;
    }

    if (!street.trim() && !name.trim()) {
      setErrorMessage("Vul ten minste een adres of een projectnaam in.");
      return;
    }

    try {
      const project = await createProjectMutation.mutateAsync({
        client_id: client.id as Did,
        address_json: {
          street: street.trim() || undefined,
          zipcode: zipcode.trim() || undefined,
          city: city.trim() || undefined,
          country: client.address_json?.country ?? "NL",
        },
        name: name.trim() || undefined,
      });
      onCreated(project);
      onClose();
    } catch (e: unknown) {
      const message =
        (e as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ?? "Project kon niet worden aangemaakt.";
      setErrorMessage(message);
    }
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose}>
      <AppHeader title="Nieuw project" onClose={onClose} />
      <IonContent>
        {step === "client" && (
          <>
            <IonSearchbar
              value={clientSearch}
              onIonInput={(e) => setClientSearch(e.detail.value!)}
              placeholder="Zoek klant..."
            />
            <IonItem
              button
              lines="full"
              onClick={() => {
                setErrorMessage(null);
                setStep("newClient");
              }}
            >
              <IonIcon slot="start" icon={addCircleOutline} color="primary" />
              <IonLabel color="primary">
                <h3 className="ion-text-bold">Nieuwe klant toevoegen</h3>
              </IonLabel>
            </IonItem>
            {clientsLoading && allClients.length === 0 ? (
              <div className="ion-padding ion-text-center">
                <IonSpinner name="bubbles" />
              </div>
            ) : (
              <>
                <IonList>
                  {allClients.map((c) => (
                    <IonItem button key={c.id} onClick={() => pickClient(c)}>
                      <IonLabel>
                        <h3>{c.name}</h3>
                        {c.address_json?.city && (
                          <p>
                            {c.address_json.street}, {c.address_json.city}
                          </p>
                        )}
                      </IonLabel>
                    </IonItem>
                  ))}
                  {allClients.length === 0 && (
                    <IonItem>
                      <IonLabel>
                        <IonText color="medium">
                          {debouncedSearch
                            ? "Geen klanten gevonden."
                            : "Nog geen klanten."}
                        </IonText>
                      </IonLabel>
                    </IonItem>
                  )}
                </IonList>

                <IonInfiniteScroll
                  onIonInfinite={async (ev) => {
                    if (hasNextPage && !isFetchingNextPage) {
                      await fetchNextPage();
                    }
                    ev.target.complete();
                  }}
                  disabled={!hasNextPage}
                >
                  <IonInfiniteScrollContent
                    loadingText="Meer klanten laden..."
                    loadingSpinner="bubbles"
                  />
                </IonInfiniteScroll>
              </>
            )}
          </>
        )}

        {step === "newClient" && (
          <IonList>
            <IonItem lines="none">
              <IonLabel>
                <h3>Nieuwe klant</h3>
                <p>Vul minimaal de naam in.</p>
              </IonLabel>
              <IonButton
                fill="clear"
                slot="end"
                onClick={() => setStep("client")}
              >
                Terug
              </IonButton>
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Naam</IonLabel>
              <IonInput
                value={newClientName}
                onIonInput={(e) => setNewClientName(e.detail.value!)}
                placeholder="Bijv. Jansen B.V."
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Straat + nr. (optioneel)</IonLabel>
              <IonInput
                value={newClientStreet}
                onIonInput={(e) => setNewClientStreet(e.detail.value!)}
                placeholder="Bijv. Hoofdstraat 12"
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Postcode</IonLabel>
              <IonInput
                value={newClientZipcode}
                onIonInput={(e) => setNewClientZipcode(e.detail.value!)}
                placeholder="1234 AB"
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Plaats</IonLabel>
              <IonInput
                value={newClientCity}
                onIonInput={(e) => setNewClientCity(e.detail.value!)}
                placeholder="Bijv. Veldhoven"
              />
            </IonItem>

            {errorMessage && (
              <IonItem lines="none">
                <IonText color="danger">{errorMessage}</IonText>
              </IonItem>
            )}

            <div className="ion-padding">
              <IonButton
                expand="block"
                disabled={creatingClient}
                onClick={handleCreateClient}
              >
                {creatingClient ? "Aanmaken..." : "Klant aanmaken"}
              </IonButton>
            </div>
          </IonList>
        )}

        {step === "details" && client && (
          <IonList>
            <IonItem lines="none">
              <IonLabel>
                <p>Klant</p>
                <h3>{client.name}</h3>
              </IonLabel>
              <IonButton
                fill="clear"
                slot="end"
                onClick={() => setStep("client")}
              >
                Wijzig
              </IonButton>
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Straat + nr.</IonLabel>
              <IonInput
                value={street}
                onIonInput={(e) => setStreet(e.detail.value!)}
                placeholder="Bijv. Hoofdstraat 12"
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Postcode</IonLabel>
              <IonInput
                value={zipcode}
                onIonInput={(e) => setZipcode(e.detail.value!)}
                placeholder="1234 AB"
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Plaats</IonLabel>
              <IonInput
                value={city}
                onIonInput={(e) => setCity(e.detail.value!)}
                placeholder="Bijv. Veldhoven"
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Naam (optioneel)</IonLabel>
              <IonInput
                value={name}
                onIonInput={(e) => setName(e.detail.value!)}
                placeholder="Wordt gevuld vanuit het adres als leeg"
              />
            </IonItem>

            {errorMessage && (
              <IonItem lines="none">
                <IonText color="danger">{errorMessage}</IonText>
              </IonItem>
            )}

            <div className="ion-padding">
              <IonButton
                expand="block"
                disabled={createProjectMutation.isPending}
                onClick={handleSubmit}
              >
                {createProjectMutation.isPending
                  ? "Aanmaken..."
                  : "Project aanmaken"}
              </IonButton>
            </div>
          </IonList>
        )}
      </IonContent>
    </IonModal>
  );
};

export default CreateProjectModal;
